import { createFileRoute, Link } from "@tanstack/react-router";
import { Footer } from "@/components/ui/footer";
import { Navbar } from "@/components/ui/navbar";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://wayra.cc";

export const Route = createFileRoute("/terms")({
  component: TermsOfServicePage,
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }],
  }),
});

function TermsOfServicePage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 px-6 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-600 mb-12">
            <strong>Last Updated: February 24, 2026</strong>
          </p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 mb-4">
              These Terms of Service ("Terms") govern your access to and use of
              the website at wayra.cc and related services (collectively, the
              "Service") operated by Wayra Labs Inc. ("Wayra Labs," "we," "us,"
              or "our"). Wayra is a product of Wayra Labs Inc.
            </p>

            <p className="text-slate-700 mb-12">
              By accessing or using the Service, you agree to be bound by these
              Terms. If you do not agree to these Terms, do not use the Service.
            </p>

            <hr className="border-slate-200 my-12" />

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                1. The Service
              </h2>

              <p className="text-slate-700 mb-6">
                Wayra is a cycling and endurance event discovery platform. The
                Service allows users to browse and search event listings, build
                race calendars, and access event-related information. The
                Service also allows event organizers to claim, enrich, and
                manage event listings, and may facilitate event registration.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Third-Party Events
              </h3>

              <p className="text-slate-700 mb-4">
                The majority of events listed on the Service are organized,
                operated, and managed by independent third-party organizers
                ("Event Organizers") and may be sourced from third-party
                registration platforms ("Registration Platforms"). Unless an
                event is explicitly identified as organized by Wayra, Wayra does
                not organize, operate, or control listed events. Event
                Organizers are solely responsible for their events, including
                but not limited to event safety, logistics, execution,
                cancellation, and refund policies. If you have any issues or
                disputes regarding a third-party event, you agree to address and
                resolve these with the Event Organizer, not with Wayra.
              </p>

              <p className="text-slate-700 mb-6">
                The display of an event on the Service does not in any way
                imply, suggest, or constitute a recommendation or endorsement by
                Wayra of that event or its organizer, nor does it imply any
                sponsorship, approval, or affiliation between the Event
                Organizer and Wayra. Events may appear on the Service without
                the knowledge or involvement of their organizer.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Event Information
              </h3>

              <p className="text-slate-700 mb-4">
                The Service aggregates publicly available event information from
                third-party sources, including registration platforms and public
                calendars. While we make reasonable efforts to ensure the
                accuracy of event listings, we do not guarantee that event
                information is complete, accurate, or up to date.{" "}
                <strong>
                  You should always verify event details directly with the event
                  organizer or registration platform before making travel or
                  participation decisions.
                </strong>
              </p>

              <p className="text-slate-700 mb-6">
                We are not the organizer of any third-party event listed on the
                Service unless explicitly stated. We are not responsible for the
                organization, safety, cancellation, or refund policies of
                third-party events.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Event Data Aggregation
              </h3>

              <p className="text-slate-700 mb-4">
                Wayra aggregates publicly available factual information about
                cycling and endurance events from third-party registration
                platforms, public calendars, and other publicly accessible
                sources. This information includes event names, dates,
                locations, distances, entry fees, and registration URLs. This
                factual data is not owned by any single platform and is
                aggregated for the purpose of providing a comprehensive event
                discovery service.
              </p>

              <p className="text-slate-700 mb-4">
                Wayra does not reproduce copyrighted content, including event
                descriptions, images, or logos, from third-party sources without
                authorization. When event data is displayed on the Service, it
                is presented as factual information with attribution and links
                to the original registration platform.
              </p>

              <p className="text-slate-700 mb-6">
                Event organizers who wish to enrich, correct, or remove their
                event listing may do so at any time by claiming their event or
                contacting us at{" "}
                <a
                  href="mailto:support@wayra.cc"
                  className="text-blue-600 hover:underline"
                >
                  support@wayra.cc
                </a>
                .
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Accuracy of Event Information
              </h3>

              <p className="text-slate-700 mb-6">
                Event information displayed on the Service is sourced from
                publicly available third-party data and is provided for
                informational purposes only. Wayra is in no way responsible for
                the accuracy, timeliness, or completeness of event information,
                including but not limited to event dates, locations, pricing,
                availability, distances, or course details. Event details are
                subject to change without notice by Event Organizers or
                Registration Platforms, and Wayra has no obligation to update
                listings in real time. Always confirm details directly with the
                Event Organizer or original Registration Platform before making
                participation, travel, or financial decisions.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Content Removal Requests
              </h3>

              <p className="text-slate-700">
                If you believe any content on the Service infringes your
                intellectual property rights or contains information you have
                the authority to request removal of, please contact us at{" "}
                <a
                  href="mailto:support@wayra.cc"
                  className="text-blue-600 hover:underline"
                >
                  support@wayra.cc
                </a>{" "}
                with a description of the content and the basis for your
                request. We will review and respond to all removal requests
                promptly.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                2. Accounts
              </h2>

              <p className="text-slate-700 mb-4">
                You may need to create an account to access certain features of
                the Service. You are responsible for maintaining the
                confidentiality of your account credentials and for all activity
                that occurs under your account. You agree to provide accurate
                and complete information when creating your account and to
                update it as necessary.
              </p>

              <p className="text-slate-700 mb-4">
                You must be at least 18 years old to create an account. By
                creating an account, you represent that you are at least 18
                years of age.
              </p>

              <p className="text-slate-700">
                We reserve the right to suspend or terminate your account at our
                discretion if we believe you have violated these Terms.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                3. Event Organizers
              </h2>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Claiming and Creating Events
              </h3>

              <p className="text-slate-700 mb-4">
                If you are an event organizer, you may claim an existing event
                listing or create a new listing on the Service. By claiming or
                creating an event, you represent and warrant that:
              </p>

              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-6">
                <li>
                  You are authorized to act on behalf of the event and its
                  organizing entity.
                </li>
                <li>
                  All information you provide about the event is accurate and
                  complete.
                </li>
                <li>
                  You have the right to upload and display any content you
                  submit, including images, route files, descriptions, and
                  logos.
                </li>
                <li>
                  You will keep your event listing current and will promptly
                  update it if details change, including cancellations.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Native Registration Hosting
              </h3>

              <p className="text-slate-700 mb-4">
                If you use Wayra's native registration hosting feature,
                additional terms apply:
              </p>

              <ul className="space-y-4 mb-6">
                <li className="text-slate-700">
                  <strong>Fees.</strong> Wayra charges a platform fee of 5% of
                  each registration, plus payment processing costs. Fees are
                  deducted before payout. The current fee schedule is available
                  on the Service and may be updated with 30 days' notice.
                </li>
                <li className="text-slate-700">
                  <strong>Payouts.</strong> Registration revenue (minus
                  applicable fees) will be disbursed to you according to the
                  payout schedule described at the time of setup.
                </li>
                <li className="text-slate-700">
                  <strong>Refunds and Cancellations.</strong> You are solely
                  responsible for your event's refund and cancellation policies.
                  You must clearly communicate these policies to registrants
                  through your event listing. If you cancel an event, you are
                  responsible for issuing refunds to registrants.
                </li>
                <li className="text-slate-700">
                  <strong>Participant Data.</strong> You will receive
                  registration data for participants who register through the
                  Service. You agree to handle participant data in compliance
                  with applicable privacy laws and to use it only for purposes
                  related to the event.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Removal of Listings
              </h3>

              <p className="text-slate-700">
                We reserve the right to remove or modify any event listing that
                violates these Terms, contains inaccurate information, or is
                reported as fraudulent. Event organizers may request removal of
                their listing at any time by contacting us at{" "}
                <a
                  href="mailto:support@wayra.cc"
                  className="text-blue-600 hover:underline"
                >
                  support@wayra.cc
                </a>
                .
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                4. Event Registrants
              </h2>

              <p className="text-slate-700 mb-4">
                If you register for an event through the Service:
              </p>

              <ul className="space-y-4">
                <li className="text-slate-700">
                  <strong>
                    Your relationship is with the event organizer.
                  </strong>{" "}
                  Wayra facilitates registration but is not a party to the
                  transaction between you and the event organizer. The organizer
                  is responsible for the event, including its safety, execution,
                  and refund policies.
                </li>
                <li className="text-slate-700">
                  <strong>Payment.</strong> Your payment is processed by our
                  third-party payment processor. By submitting payment, you
                  agree to the payment processor's terms of service.
                </li>
                <li className="text-slate-700">
                  <strong>Refunds.</strong> Refund eligibility is determined by
                  the event organizer's stated refund policy. Wayra's platform
                  fee is non-refundable. Disputes about refunds should be
                  directed to the event organizer.
                </li>
                <li className="text-slate-700">
                  <strong>Assumption of Risk.</strong> Participation in cycling
                  and endurance events involves inherent risks, including
                  serious injury or death. By registering for an event through
                  the Service, you acknowledge these risks. Any waivers or
                  liability releases required by the event organizer are between
                  you and the organizer.
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                5. User Content
              </h2>

              <p className="text-slate-700 mb-4">
                You may submit content to the Service, including event
                descriptions, images, route files, reviews, and comments ("User
                Content"). By submitting User Content, you grant Wayra Labs Inc.
                a non-exclusive, worldwide, royalty-free, sublicensable license
                to use, display, reproduce, and distribute your User Content in
                connection with operating and promoting the Service.
              </p>

              <p className="text-slate-700 mb-4">
                You retain ownership of your User Content. You represent and
                warrant that you own or have the necessary rights to submit your
                User Content and that it does not infringe the intellectual
                property or other rights of any third party.
              </p>

              <p className="text-slate-700">
                We reserve the right to remove any User Content that violates
                these Terms or that we find objectionable, at our sole
                discretion.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                6. Prohibited Conduct
              </h2>

              <p className="text-slate-700 mb-4">You agree not to:</p>

              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Use the Service for any unlawful purpose</li>
                <li>
                  Submit false, misleading, or fraudulent event listings or
                  account information
                </li>
                <li>
                  Use any manual or automated process to access the Service for
                  any purpose other than your personal, non-commercial use,
                  including but not limited to use of any automated system,
                  software, or script to extract data from the Service ("screen
                  scraping") for commercial or non-commercial purposes, without
                  our prior express written permission
                </li>
                <li>
                  Violate the restrictions in any robot exclusion headers on the
                  Service or bypass or circumvent other measures employed to
                  prevent or limit access to the Service
                </li>
                <li>
                  Use any device, software, or routine that interferes or
                  attempts to interfere with the normal operation of the
                  Service, or take any action that imposes an unreasonable load
                  on our infrastructure
                </li>
                <li>
                  Reproduce, duplicate, copy, sell, trade, resell, or exploit
                  the Service or any data obtained from the Service for any
                  commercial purpose without our prior written consent
                </li>
                <li>
                  Interfere with or disrupt the Service or its infrastructure
                </li>
                <li>
                  Attempt to gain unauthorized access to any part of the Service
                </li>
                <li>Impersonate any person or entity</li>
                <li>
                  Use the Service to send unsolicited communications or spam
                </li>
                <li>
                  Upload content that is defamatory, obscene, or infringes on
                  the rights of others
                </li>
                <li>
                  Deep-link to any portion of the Service for any purpose
                  without our prior written consent
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 7 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                7. Intellectual Property
              </h2>

              <p className="text-slate-700 mb-4">
                The Service, including its design, code, branding, logos, and
                original content, is owned by Wayra Labs Inc. and is protected
                by copyright, trademark, and other intellectual property laws.
                You may not copy, modify, distribute, or create derivative works
                based on the Service without our prior written consent.
              </p>

              <p className="text-slate-700 mb-4">
                Event data aggregated from third-party public sources consists
                of factual information and is displayed for informational
                purposes. Wayra does not claim ownership of third-party event
                data.
              </p>

              <p className="text-slate-700">
                The name "Wayra" and associated logos are trademarks of Wayra
                Labs Inc. You may not use our trademarks without prior written
                permission.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 8 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                8. Third-Party Services and Links
              </h2>

              <p className="text-slate-700">
                The Service contains links to third-party websites and services,
                including event registration platforms. We do not control and
                are not responsible for the content, privacy practices, or
                availability of third-party services. Your use of third-party
                services is at your own risk and subject to their respective
                terms.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 9 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                9. Disclaimer of Warranties
              </h2>

              <p className="text-slate-700 uppercase mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
                NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <p className="text-slate-700 uppercase">
                WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
                SECURE, OR ERROR-FREE. WE DO NOT WARRANT THE ACCURACY,
                COMPLETENESS, OR RELIABILITY OF ANY EVENT INFORMATION DISPLAYED
                ON THE SERVICE.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 10 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                10. Limitation of Liability
              </h2>

              <p className="text-slate-700 uppercase mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, Wayra Labs Inc. AND ITS
                OFFICERS, MEMBERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR
                GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
              </p>

              <p className="text-slate-700 uppercase mb-4">
                OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO
                THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A)
                THE AMOUNT YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE
                CLAIM OR (B) ONE HUNDRED DOLLARS ($100).
              </p>

              <p className="text-slate-700 uppercase">
                THIS LIMITATION APPLIES REGARDLESS OF THE THEORY OF LIABILITY,
                WHETHER BASED ON WARRANTY, CONTRACT, TORT, NEGLIGENCE, STRICT
                LIABILITY, OR ANY OTHER LEGAL THEORY.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 11 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                11. Indemnification
              </h2>

              <p className="text-slate-700 mb-4">
                You agree to indemnify, defend, and hold harmless Wayra Labs
                Inc. and its officers, members, employees, and agents from any
                claims, liabilities, damages, losses, and expenses (including
                reasonable attorneys' fees) arising out of or related to:
              </p>

              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your User Content</li>
                <li>
                  Your event (if you are an organizer using native registration
                  hosting)
                </li>
                <li>
                  Any dispute between you and an event organizer or registrant
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 12 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                12. Governing Law and Disputes
              </h2>

              <p className="text-slate-700">
                These Terms are governed by the laws of the State of New York,
                without regard to its conflict of laws provisions. Any disputes
                arising out of or related to these Terms or the Service shall be
                resolved exclusively in the state or federal courts located in
                Kings County, New York. You consent to the personal jurisdiction
                of these courts.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 13 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                13. Changes to These Terms
              </h2>

              <p className="text-slate-700 mb-4">
                We may update these Terms from time to time. We will notify you
                of material changes by posting the updated Terms on the Service
                and updating the "Last Updated" date. Your continued use of the
                Service after changes are posted constitutes your acceptance of
                the updated Terms.
              </p>

              <p className="text-slate-700">
                If you do not agree with the updated Terms, you must stop using
                the Service.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 14 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                14. Termination
              </h2>

              <p className="text-slate-700">
                We may suspend or terminate your access to the Service at any
                time, with or without cause, and with or without notice. Upon
                termination, your right to use the Service ceases immediately.
                Sections 5 (User Content license), 9 (Disclaimers), 10
                (Limitation of Liability), 11 (Indemnification), and 12
                (Governing Law) survive termination.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 15 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                15. General
              </h2>

              <ul className="space-y-4">
                <li className="text-slate-700">
                  <strong>Entire Agreement.</strong> These Terms, together with
                  our{" "}
                  <Link to="/privacy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                  , constitute the entire agreement between you and Wayra Labs
                  Inc. regarding the Service.
                </li>
                <li className="text-slate-700">
                  <strong>Severability.</strong> If any provision of these Terms
                  is held to be unenforceable, the remaining provisions remain
                  in full force and effect.
                </li>
                <li className="text-slate-700">
                  <strong>Waiver.</strong> Our failure to enforce any right or
                  provision of these Terms does not constitute a waiver of that
                  right or provision.
                </li>
                <li className="text-slate-700">
                  <strong>Assignment.</strong> You may not assign your rights or
                  obligations under these Terms without our prior written
                  consent. We may assign our rights and obligations without
                  restriction.
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 16 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                16. Contact Us
              </h2>

              <p className="text-slate-700 mb-4">
                If you have questions about these Terms, please contact us at:
              </p>

              <div className="text-slate-700">
                <p className="font-semibold">Wayra Labs Inc.</p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:support@wayra.cc"
                    className="text-blue-600 hover:underline"
                  >
                    support@wayra.cc
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
